import React from 'react';
import styled, { keyframes } from 'styled-components';
import { motion } from 'framer-motion';
import Color from '@styles/Color';

// ─────────────────────────────────────────────
// 공통 타입
// ─────────────────────────────────────────────
export interface ArrowButtonDesignProps {
  color?: string;           // button: 화살표 기본 컬러
  backgroundColor?: string; // bg: 배경이 있는 디자인만 사용
  pointColor?: string;      // point: 장식/테두리/글로우 등 포인트 요소
  direction?: 'left' | 'right';
}

/** direction='right'이면 좌우 반전하는 래퍼 */
const DirectionWrap = styled.span<{ $dir?: 'left' | 'right' }>`
  display: inline-flex;
  transform: ${(p) => (p.$dir === 'right' ? 'scaleX(-1)' : 'none')};
`;

// ─────────────────────────────────────────────
// 디자인 1: DiaArrow (기본값)
// button: 화살표 stroke
// point: 다이아몬드 도트 + 라인 장식
// bg: 없음
// ─────────────────────────────────────────────
const DiaWrap = styled.div`
  width: 56px;
  padding: 12px 0;
  display: flex;
  justify-content: center;
  align-items: center;

  &:hover {
    cursor: var(--hoverCursorURL), pointer;
    .diaArrowWrap .arrow {
      transform: translateX(-3px);
    }
    .diaArrowWrap .diamond-dot {
      left: 0;
      transform: rotate(135deg);
    }
    .diaArrowWrap .diamond-line {
      width: 20px;
      transform: translateX(-16px);
    }
  }
`;

const DiaArrowInner = styled.div<{ $color?: string; $point?: string }>`
  --btn-color: ${(p) => p.$color || 'var(--textColor)'};
  --btn-point: ${(p) => p.$point || p.$color || 'var(--textColor)'};
  position: relative;

  .arrow {
    will-change: transform;
    transition: all 0.3s ease;
    path { stroke: var(--btn-color); }
  }
  .diamond-dot {
    width: 6px;
    height: 6px;
    background-color: var(--btn-color);
    transform: rotate(45deg);
    will-change: transform, left;
    transition: all 0.2s ease;
    position: absolute;
    left: 10px;
    top: 8px;
  }
  .diamond-line {
    display: inline-block;
    border-top: 1px solid var(--btn-color);
    width: 16px;
    will-change: transform, width;
    transition: all 0.3s ease;
    position: absolute;
    top: 10px;
    left: 22px;
  }
`;

export const DiaArrowDesign: React.FC<ArrowButtonDesignProps> = ({
  color,
  pointColor,
  direction = 'left',
}) => (
  <DirectionWrap $dir={direction}>
    <DiaWrap>
      <DiaArrowInner className="diaArrowWrap" $color={color} $point={pointColor}>
        <svg width="13" height="22" viewBox="0 0 13 22" fill="none" className="arrow">
          <path d="M11.3838 0.888977L1.23625 11.0365L11.3838 21.184" strokeWidth="2" strokeLinejoin="round" />
        </svg>
        <motion.div className="diamond-dot" />
        <motion.div className="diamond-line" />
      </DiaArrowInner>
    </DiaWrap>
  </DirectionWrap>
);

// ─────────────────────────────────────────────
// 디자인 2: CircleArrow
// button: 화살표 stroke
// point: 원 테두리 + 호버 시 fill
// bg: 원 내부 배경
// ─────────────────────────────────────────────
const CircleWrap = styled.div<{ $color?: string; $bg?: string; $point?: string }>`
  --btn-color: ${(p) => p.$color || 'var(--textColor)'};
  --btn-bg: ${(p) => p.$bg || 'transparent'};
  --btn-point: ${(p) => p.$point || p.$color || 'var(--textColor)'};
  width: 42px;
  height: 42px;
  border-radius: 50%;
  border: 2px solid var(--btn-point);
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--btn-bg);
  transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;

  svg {
    position: relative;
    z-index: 1;
    transition: transform 0.3s ease;
    path {
      stroke: var(--btn-color);
      transition: stroke 0.3s ease;
    }
  }

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 50%;
    background: var(--btn-point);
    transform: scale(0);
    transition: transform 0.35s cubic-bezier(0.4, 0, 0.2, 1);
  }

  &:hover {
    cursor: var(--hoverCursorURL), pointer;
    &::before { transform: scale(1); }
    svg {
      transform: translateX(-2px);
      path { stroke: var(--btn-bg, var(--cardBackgroundColor, #fff)); }
    }
  }
`;

export const CircleArrowDesign: React.FC<ArrowButtonDesignProps> = ({
  color,
  backgroundColor,
  pointColor,
  direction = 'left',
}) => (
  <DirectionWrap $dir={direction}>
    <CircleWrap $color={color} $bg={backgroundColor} $point={pointColor}>
      <svg width="12" height="20" viewBox="0 0 12 20" fill="none">
        <path d="M10 2L2 10L10 18" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </CircleWrap>
  </DirectionWrap>
);

// ─────────────────────────────────────────────
// 디자인 3: SlideArrow
// button: 화살표 stroke + 슬라이드 라인
// point: 없음 (심플 디자인)
// bg: 없음
// ─────────────────────────────────────────────
const SlideWrap = styled.div<{ $color?: string }>`
  --btn-color: ${(p) => p.$color || 'var(--textColor)'};
  display: flex;
  align-items: center;
  gap: 0;
  padding: 10px 14px;
  position: relative;

  .slide-arrow { transition: transform 0.3s ease; }
  .slide-line {
    width: 0;
    height: 2px;
    background: var(--btn-color);
    transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    border-radius: 1px;
  }

  &:hover {
    cursor: var(--hoverCursorURL), pointer;
    .slide-arrow { transform: translateX(-4px); }
    .slide-line { width: 24px; }
  }
`;

export const SlideArrowDesign: React.FC<ArrowButtonDesignProps> = ({
  color,
  direction = 'left',
}) => (
  <DirectionWrap $dir={direction}>
    <SlideWrap $color={color}>
      <svg className="slide-arrow" width="14" height="14" viewBox="0 0 14 14" fill="none">
        <path d="M8 1L1.5 7L8 13" stroke="var(--btn-color)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      <div className="slide-line" />
    </SlideWrap>
  </DirectionWrap>
);

// ─────────────────────────────────────────────
// 디자인 4: RuneArrow (게임 UI - 마법 룬)
// button: 화살표 stroke
// point: 룬 서클 + 마크 + 파티클 + 에너지 꼬리
// bg: 원 배경
// ─────────────────────────────────────────────
const runeRotate = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;
const runeGlow = keyframes`
  0%, 100% { opacity: 0.4; filter: drop-shadow(0 0 2px var(--btn-point)); }
  50% { opacity: 0.9; filter: drop-shadow(0 0 6px var(--btn-point)); }
`;
const runeParticle = keyframes`
  0% { transform: translate(0, 0) scale(1); opacity: 0.8; }
  50% { opacity: 1; }
  100% { transform: translate(-12px, -6px) scale(0); opacity: 0; }
`;

const RuneWrap = styled.div<{ $color?: string; $bg?: string; $point?: string }>`
  --btn-color: ${(p) => p.$color || 'var(--textColor)'};
  --btn-bg: ${(p) => p.$bg || 'rgba(10, 15, 30, 0.7)'};
  --btn-point: ${(p) => p.$point || p.$color || 'var(--textColor)'};
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--btn-bg);
  position: relative;
  overflow: visible;

  .rune-circle {
    position: absolute;
    inset: -1px;
    border-radius: 50%;
    border: 1.5px solid transparent;
    border-top-color: var(--btn-point);
    border-bottom-color: var(--btn-point);
    animation: ${runeRotate} 6s linear infinite;
    opacity: 0.6;
  }
  .rune-inner-circle {
    position: absolute;
    inset: 4px;
    border-radius: 50%;
    border: 1px dashed var(--btn-point);
    animation: ${runeRotate} 8s linear infinite reverse;
    opacity: 0.25;
  }
  .rune-arrow {
    position: relative;
    z-index: 2;
    transition: transform 0.3s ease;
    animation: ${runeGlow} 3s ease-in-out infinite;
  }
  .rune-particle {
    position: absolute;
    width: 3px;
    height: 3px;
    border-radius: 50%;
    background: var(--btn-point);
    opacity: 0;
    z-index: 3;
    &.p1 { top: 35%; left: 25%; }
    &.p2 { top: 55%; left: 20%; }
    &.p3 { top: 45%; left: 30%; }
  }
  .rune-mark {
    position: absolute;
    width: 4px;
    height: 4px;
    background: var(--btn-point);
    opacity: 0.3;
    transition: opacity 0.3s ease;
    &.top { top: 2px; left: 50%; transform: translateX(-50%) rotate(45deg); }
    &.bottom { bottom: 2px; left: 50%; transform: translateX(-50%) rotate(45deg); }
    &.left { left: 2px; top: 50%; transform: translateY(-50%) rotate(45deg); }
    &.right { right: 2px; top: 50%; transform: translateY(-50%) rotate(45deg); }
  }

  &:hover {
    cursor: var(--hoverCursorURL), pointer;
    .rune-circle { opacity: 1; border-left-color: var(--btn-point); }
    .rune-inner-circle { opacity: 0.5; }
    .rune-arrow { transform: translateX(-3px); filter: drop-shadow(0 0 8px var(--btn-point)); }
    .rune-particle {
      &.p1 { animation: ${runeParticle} 0.8s ease-out 0s forwards; }
      &.p2 { animation: ${runeParticle} 0.8s ease-out 0.15s forwards; }
      &.p3 { animation: ${runeParticle} 0.8s ease-out 0.3s forwards; }
    }
    .rune-mark { opacity: 0.8; }
  }
`;

export const RuneArrowDesign: React.FC<ArrowButtonDesignProps> = ({
  color,
  backgroundColor,
  pointColor,
  direction = 'left',
}) => (
  <DirectionWrap $dir={direction}>
    <RuneWrap $color={color} $bg={backgroundColor} $point={pointColor}>
      <div className="rune-circle" />
      <div className="rune-inner-circle" />
      <div className="rune-mark top" />
      <div className="rune-mark bottom" />
      <div className="rune-mark left" />
      <div className="rune-mark right" />
      <div className="rune-particle p1" />
      <div className="rune-particle p2" />
      <div className="rune-particle p3" />
      <svg className="rune-arrow" width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path d="M10 2.5L4 8L10 13.5" stroke="var(--btn-color)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M5 8H13" stroke="var(--btn-point)" strokeWidth="1" strokeLinecap="round" opacity="0.5" strokeDasharray="2 3" />
      </svg>
    </RuneWrap>
  </DirectionWrap>
);

// ─────────────────────────────────────────────
// 디자인 5: PixelArrow (게임 UI - 레트로/픽셀)
// button: 픽셀 화살표
// point: 테두리 + 그림자 + 반짝이
// bg: 배경
// ─────────────────────────────────────────────
const pixelBlink = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.6; }
`;

const PixelWrap = styled.div<{ $color?: string; $bg?: string; $point?: string }>`
  --btn-color: ${(p) => p.$color || 'var(--textColor)'};
  --btn-bg: ${(p) => p.$bg || 'transparent'};
  --btn-point: ${(p) => p.$point || p.$color || 'var(--textColor)'};
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--btn-bg);
  image-rendering: pixelated;
  position: relative;
  border: 3px solid var(--btn-point);
  box-shadow:
    3px 3px 0 0 var(--btn-point),
    -1px -1px 0 0 var(--btn-point);

  .pixel-arrow { transition: transform 0.15s steps(2); }
  .pixel-sparkle {
    position: absolute;
    width: 4px;
    height: 4px;
    background: var(--btn-point);
    top: 4px;
    right: 4px;
    opacity: 0;
    animation: ${pixelBlink} 1.5s steps(2) infinite;
  }

  &:hover {
    cursor: var(--hoverCursorURL), pointer;
    box-shadow: 0 0 0 2px var(--btn-point);
    .pixel-arrow { transform: translateX(-2px); }
    .pixel-sparkle { opacity: 1; }
  }
`;

export const PixelArrowDesign: React.FC<ArrowButtonDesignProps> = ({
  color,
  backgroundColor,
  pointColor,
  direction = 'left',
}) => (
  <DirectionWrap $dir={direction}>
    <PixelWrap $color={color} $bg={backgroundColor} $point={pointColor}>
      <svg className="pixel-arrow" width="18" height="18" viewBox="0 0 18 18" fill="none" shapeRendering="crispEdges">
        <rect x="8" y="2" width="2" height="2" fill="var(--btn-color)" />
        <rect x="6" y="4" width="2" height="2" fill="var(--btn-color)" />
        <rect x="4" y="6" width="2" height="2" fill="var(--btn-color)" />
        <rect x="2" y="8" width="2" height="2" fill="var(--btn-color)" />
        <rect x="4" y="10" width="2" height="2" fill="var(--btn-color)" />
        <rect x="6" y="12" width="2" height="2" fill="var(--btn-color)" />
        <rect x="8" y="14" width="2" height="2" fill="var(--btn-color)" />
        <rect x="6" y="8" width="10" height="2" fill="var(--btn-color)" />
      </svg>
      <div className="pixel-sparkle" />
    </PixelWrap>
  </DirectionWrap>
);

// ─────────────────────────────────────────────
// 디자인 6: CrystalArrow ↔ CrystalButton 한 쌍
// Diamond Sign Arrow (다이아몬드 표지판 백버튼)
// button: 화살표 + 닷 컬러
// point:  다이아 외곽 테두리 / 장식 컬러
// bg:     다이아 내부 배경 컬러
//
// CrystalButton 공유 언어:
//  • .cs-ornament  ← diamond-ornament (좌/우 팁 장식 다이아)
//  • crystalSignPulse ← crystalPulse (맥동 glow ring)
//  • hover: glow drop-shadow + 장식 글로우 + 반바퀴 회전
// ─────────────────────────────────────────────

const crystalSignPulse = keyframes`
  0%, 100% { filter: drop-shadow(0 0 2px var(--btn-point)); }
  50%       { filter: drop-shadow(0 0 5px var(--btn-point)); }
`;

const DiamondSignWrap = styled.div<{ $color?: string; $bg?: string; $point?: string }>`
  --btn-color: ${(p) => p.$color || 'var(--textColor)'};
  --btn-bg:    ${(p) => p.$bg || 'var(--inkColor)'};
  --btn-point: ${(p) => p.$point || p.$color || 'var(--textColor)'};

  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;

  /* ── CrystalButton의 diamond-ornament 대응 ──
     다이아몬드 좌/우 팁 위치에 장식 다이아 배치 */
  .cs-ornament {
    position: absolute;
    top: 50%;
    width: 9px;
    height: 9px;
    transform: translateY(-50%) rotate(45deg);
    border: 1px solid var(--btn-point);
    background: var(--btn-bg);
    z-index: 2;
    pointer-events: none;
    transition: box-shadow 0.3s ease;

    &.left  { left:  0px; }
    &.right { right: 0px; }

    /* 내부 채움 (CrystalButton::before 대응) */
    &::before {
      content: '';
      position: absolute;
      inset: 1.5px;
      background: var(--btn-point);
      opacity: 0.25;
      transition: opacity 0.3s ease;
    }
  }

  /* ── CrystalButton의 crystalPulse 대응 ──
     drop-shadow로 다이아 윤곽 전체에 맥동 glow */
  svg {
    overflow: visible;
    animation: ${crystalSignPulse} 3s ease-in-out infinite;
  }

  /* ── 다이아 외형: hover 시 달칵 회전 ── */
  .ds-diamond {
    transform-box: fill-box;
    transform-origin: center;
    transition: transform 0.22s cubic-bezier(0.3, 1.8, 0.5, 1);
  }

  /* ── 화살표 + 닷: 고정 ── */
  .ds-arrow {
    transform-box: fill-box;
    transform-origin: center;
  }

  /* ── hover (CrystalButton hover 동일 언어) ── */
  &:hover {
    cursor: var(--hoverCursorURL), pointer;

    /* 다이아 달칵 회전 (45°) */
    .ds-diamond {
      transform: rotate(45deg);
      border-radius: 50%;
    }

    /* 강한 glow (CrystalButton의 box-shadow 대응) */
    svg {
      filter: drop-shadow(0 0 10px var(--btn-point));
      animation-play-state: paused;
    }

    /* 장식 다이아 글로우 + 내부 채움 강조 */
    .cs-ornament {
      box-shadow: 0 0 8px var(--btn-point);
      &::before { opacity: 0.85; }
    }
  }
`;

export const CrystalArrowDesign: React.FC<ArrowButtonDesignProps> = ({
  color,
  backgroundColor,
  pointColor,
  direction = 'left',
}) => (
  <DirectionWrap $dir={direction}>
    <DiamondSignWrap $color={color} $bg={backgroundColor} $point={pointColor}>
      {/* CrystalButton diamond-ornament 대응: 좌/우 팁 장식 */}
      <span className="cs-ornament left" />
      <span className="cs-ornament right" />

      <svg width="46" height="46" viewBox="0 0 241 241" fill="none">
        {/* 다이아 외형 - hover 시 반바퀴 회전 */}
        <g className="ds-diamond">
          <rect
            x="120.272" y="17.2725"
            width="145.664" height="145.664"
            rx="9"
            transform="rotate(45 120.272 17.2725)"
            fill="var(--btn-bg)"
          />
          <rect
            x="120.272" y="1.92932"
            width="167.362" height="167.362"
            rx="5"
            transform="rotate(45 120.272 1.92932)"
            stroke="var(--btn-point)"
            strokeWidth="4"
            fill="none"
          />
        </g>

        {/* 화살표 + 닷 - 고정 */}
        <g className="ds-arrow">
          <path
            d="M120.772 87.7725L92.772 115.772L120.772 143.772"
            stroke="var(--btn-color)"
            strokeWidth="11"
            strokeLinecap="round"
          />
          <rect
            x="127.318" y="103.318"
            width="17.7427" height="17.7427"
            transform="rotate(45 127.318 103.318)"
            fill="var(--btn-color)"
          />
        </g>
      </svg>
    </DiamondSignWrap>
  </DirectionWrap>
);
